'use client';

import { useState, useCallback, useRef, useEffect } from 'react';
import type {
  ProductModel,
  ConfigurationState,
  ConfigAction,
  ConfigActionType,
  SetAttributeValuePayload,
  SelectComponentPayload,
} from '@config3d/contracts';
import { generateId, createContractContext } from '@config3d/contracts';
import { evaluateRuleGraphsForSelections, type SerializedGraph, type RuleAction } from './client-rule-eval';
import { getPlayerApiBase, prefixUploadsDeep } from './api-base';
import {
  prefetchSceneModelAssetUrls,
  warmMaterialGraphTextureCache,
  collectMaterialGraphImageUrls,
  applyPlayerTextureQualityPreset,
  ensurePlayerTextureQuality,
} from '@config3d/scene';
import { firstWaveGlbUrls, firstWaveMaterialIds, type FirstWaveRuleAction } from './first-wave-assets';

function preferMobileTex(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent.toLowerCase();
  return (
    /iphone|ipad|ipod|android/.test(ua) ||
    (ua.includes('mac') && typeof document !== 'undefined' && 'ontouchend' in document)
  );
}

function preferTabletTex(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent.toLowerCase();
  const isIOS =
    /iphone|ipad|ipod/.test(ua) ||
    (ua.includes('mac') && typeof document !== 'undefined' && 'ontouchend' in document);
  if (isIOS) {
    return /ipad/.test(ua) || (ua.includes('mac') && typeof document !== 'undefined' && 'ontouchend' in document);
  }
  return /android/.test(ua) && !/mobile/.test(ua);
}

/** URL `?tex=1k` must win — otherwise scene prefetch resets quality to full and oak maps download at 4k. */
function urlTextureQualityPreset(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const v = new URLSearchParams(window.location.search).get('tex');
    return v && v.trim() ? v.trim() : null;
  } catch {
    return null;
  }
}

/** Match ConfiguratorPlayer texOpts so the first TextureLoader hit is already 2k/1k, not full-res. */
function applySceneTexOptsForPrefetch(scene: SceneData | null | undefined): void {
  const raw = (scene?.metadata as {
    texOpts?: { desktop?: string; mobile?: string; tablet?: string; webp?: boolean };
  } | undefined)?.texOpts;
  const desktop = raw?.desktop || 'full';
  const mobile = raw?.mobile || '2k';
  const tablet = raw?.tablet || mobile || '2k';
  const urlTex = urlTextureQualityPreset();
  const devicePreset = preferMobileTex() ? (preferTabletTex() ? tablet : mobile) : desktop;
  applyPlayerTextureQualityPreset(urlTex || devicePreset, { silent: true });
  ensurePlayerTextureQuality({
    webp: raw?.webp !== false,
    cpuBakeMaxEdge: preferMobileTex() ? (preferTabletTex() ? 1024 : 512) : undefined,
  });
}

function sceneRuleActions(derived: unknown): FirstWaveRuleAction[] {
  const actions = (derived as { ruleActions?: FirstWaveRuleAction[] } | null | undefined)?.ruleActions;
  return Array.isArray(actions) ? actions : [];
}

/** Start decoding only materials on the first-paint objects (parallel with those GLBs). */
function prefetchAssignedGraphTextures(
  scene: SceneData | null | undefined,
  mats: any[],
  ruleActions: FirstWaveRuleAction[],
): void {
  if (!Array.isArray(mats) || mats.length === 0 || !scene?.objects?.length) return;
  const assignments = (
    scene.metadata as {
      materialAssignments?: Array<{ materialId?: string; targets?: Array<{ type?: string; objectId?: string }> }>;
    } | undefined
  )?.materialAssignments;
  const assignedIds = firstWaveMaterialIds(scene.objects, ruleActions, assignments);
  // Avoid flooding the network with every library graph — only what the default view uses.
  if (assignedIds.size === 0) return;
  for (const mat of mats) {
    if (mat?.type !== 'graph' || !mat.data?.nodes) continue;
    if (!assignedIds.has(mat.id) && !assignedIds.has(mat.name)) continue;
    try {
      warmMaterialGraphTextureCache(collectMaterialGraphImageUrls(mat.data));
    } catch {
      /* ignore */
    }
  }
}

export interface SessionAssets {
  models: Array<{ id: string; name: string; url: string; format: string }>;
}

export interface VariantAssetMap {
  [variantId: string]: { assetId: string; url: string } | null;
}

export interface ComponentVariantData {
  [componentId: string]: VariantAssetMap;
}

export interface SceneObjectData {
  id: string;
  name: string;
  type: string;
  parentId?: string | null;
  assetId: string | null;
  asset: { id: string; url: string; name: string } | null;
  transform: {
    position: { x: number; y: number; z: number };
    rotation: { x: number; y: number; z: number };
    scale: { x: number; y: number; z: number };
  };
  visible: boolean;
  metadata: Record<string, unknown>;
}

export interface SceneData {
  id: string;
  name: string;
  camera: Record<string, unknown>;
  environment: {
    preset?: string;
    hdrUrl?: string;
    intensity?: number;
    backgroundBlur?: number;
    showBackground?: boolean;
    linkToCamera?: boolean;
  };
  metadata: Record<string, unknown>;
  objects: SceneObjectData[];
}

/** True if the default scene can render 3D from inline objects (not the catalog-only fallback). */
function sceneHasPlacedRenderableModels(scene: SceneData | null): boolean {
  if (!scene?.objects?.length) return false;
  for (const o of scene.objects) {
    if (o.visible === false) continue;
    if (o.type === 'primitive') {
      const shape = (o.metadata as { primitiveShape?: string } | undefined)?.primitiveShape;
      if (shape) return true;
      continue;
    }
    if (o.asset?.url) return true;
  }
  return false;
}

export function useConfigurator(productId: string, initialSelections?: Record<string, unknown>) {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [productModel, setProductModel] = useState<ProductModel | null>(null);
  const [configState, setConfigState] = useState<ConfigurationState | null>(null);
  const [assets, setAssets] = useState<SessionAssets>({ models: [] });
  const [componentVariants, setComponentVariants] = useState<ComponentVariantData>({});
  const [sceneData, setSceneData] = useState<SceneData | null>(null);
  const [libraryMaterials, setLibraryMaterials] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Avoid recreating initSession when parents pass a new selections object identity each render.
  const initialSelectionsRef = useRef(initialSelections);
  initialSelectionsRef.current = initialSelections;
  const initInflightRef = useRef(false);

  const initSession = useCallback(async () => {
    if (initInflightRef.current) return;
    initInflightRef.current = true;
    setLoading(true);
    setError(null);
    try {
      // Product GETs only need `productId` (no session cookie). Start them immediately so they
      // overlap the session POST — especially helps Safari, which used to wait for session, then
      // four GETs, then a fifth (rules), i.e. an extra RTT in the critical path.
      const base = getPlayerApiBase();
      const assetsP = fetch(`${base}/api/assets/product/${productId}`);
      const componentsP = fetch(`${base}/api/components/product/${productId}`);
      const sceneP = fetch(`${base}/api/scenes/product/${productId}/default`);
      const materialsP = fetch(`${base}/api/materials/product/${productId}`);
      const rulesP = fetch(`${base}/api/rules/product/${productId}`).catch(() => null);

      const res = await fetch(`${base}/api/configurations/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ productId, selections: initialSelectionsRef.current }),
      });
      if (!res.ok) throw new Error('Failed to create session');
      const data = await res.json();
      setSessionId(data.sessionId);
      sessionRef.current = data.sessionId;
      setProductModel(prefixUploadsDeep(data.productModel));
      configRef.current = data.configurationState;
      setConfigState(data.configurationState);

      // Consume scene first: large JSON parse + setSceneData unblock the 3D path before materials
      // / rules (often heavy) finish downloading — major win on Safari CPU + main thread.
      const sceneRes = await sceneP;
      const sceneJson = sceneRes.ok ? prefixUploadsDeep(await sceneRes.json()) : null;
      let sceneApplied: SceneData | null = null;
      if (sceneJson != null && typeof sceneJson === 'object') {
        sceneApplied = sceneJson as SceneData;
        setSceneData(sceneApplied);
        applySceneTexOptsForPrefetch(sceneApplied);
        // Only the default composition — Leather/Rubber/etc. wait until after first reveal.
        prefetchSceneModelAssetUrls(
          firstWaveGlbUrls(sceneApplied.objects, sceneRuleActions(data.configurationState?.derived)),
        );
      }

      const canPaintSceneFirst = sceneHasPlacedRenderableModels(sceneApplied);
      if (canPaintSceneFirst) {
        setLoading(false);
      }

      const [assetsRes, componentsRes, materialsRes, rulesRes] = await Promise.all([
        assetsP,
        componentsP,
        materialsP,
        rulesP,
      ]);

      const [allAssets, components] = await Promise.all([
        assetsRes.ok ? assetsRes.json().then(prefixUploadsDeep) : Promise.resolve(null),
        componentsRes.ok ? componentsRes.json().then(prefixUploadsDeep) : Promise.resolve(null),
      ]);

      if (Array.isArray(allAssets)) {
        setAssets({
          models: allAssets.filter((a: any) =>
            a.type === 'model' && (a.format === 'glb' || a.format === 'gltf')
          ),
        });
      }

      if (Array.isArray(components)) {
        const variantData: ComponentVariantData = {};
        for (const comp of components) {
          variantData[comp.id] = {};
          for (const variant of comp.variants || []) {
            variantData[comp.id][variant.id] = variant.asset
              ? { assetId: variant.asset.id, url: variant.asset.url }
              : null;
          }
        }
        setComponentVariants(variantData);
      }

      if (!canPaintSceneFirst) {
        setLoading(false);
      }

      const [mats, rules] = await Promise.all([
        materialsRes.ok ? materialsRes.json().then(prefixUploadsDeep) : Promise.resolve(null),
        rulesRes && rulesRes.ok ? rulesRes.json() : Promise.resolve(null),
      ]);

      if (Array.isArray(mats)) {
        setLibraryMaterials(mats);
        prefetchAssignedGraphTextures(
          sceneApplied,
          mats,
          sceneRuleActions(data.configurationState?.derived),
        );
      }

      try {
        if (Array.isArray(rules)) {
          ruleGraphsRef.current = (rules as Array<{ graph: unknown }>).map((r) => {
            const g = typeof r.graph === 'string' ? JSON.parse(r.graph as string) : r.graph;
            return g as SerializedGraph;
          });
        } else {
          ruleGraphsRef.current = [];
          if (process.env.NODE_ENV === 'development' && rulesRes != null && !rulesRes.ok) {
            console.warn('[configurator] Rule graphs request failed:', rulesRes.status);
          }
        }
      } catch (err) {
        ruleGraphsRef.current = [];
        if (process.env.NODE_ENV === 'development') {
          console.warn('[configurator] Rule graphs load error:', err);
        }
      }
    } catch (e: any) {
      initInflightRef.current = false;
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => {
    initInflightRef.current = false;
  }, [productId]);

  const configRef = useRef(configState);
  configRef.current = configState;
  const sessionRef = useRef(sessionId);
  sessionRef.current = sessionId;
  const pendingRef = useRef<Promise<void>>(Promise.resolve());
  /** Bumped on every optimistic selection write; stale action responses must not clobber newer local tints. */
  const selectionEpochRef = useRef(0);
  const productModelRef = useRef<ProductModel | null>(null);
  productModelRef.current = productModel;
  const ruleGraphsRef = useRef<SerializedGraph[]>([]);

  const getRuleGraphs = useCallback(() => ruleGraphsRef.current, []);

  const applyServerConfiguration = useCallback((server: ConfigurationState, epochAtSend: number) => {
    if (epochAtSend === selectionEpochRef.current) {
      configRef.current = server;
      setConfigState(server);
      return;
    }
    // A newer optimistic patch landed while this request was in flight — keep local
    // selections/rules, only adopt server revision bookkeeping.
    const local = configRef.current;
    if (!local) {
      configRef.current = server;
      setConfigState(server);
      return;
    }
    const merged: ConfigurationState = {
      ...server,
      selections: local.selections,
      derived: {
        ...server.derived,
        ruleActions: local.derived?.ruleActions ?? server.derived?.ruleActions,
      },
    };
    configRef.current = merged;
    setConfigState(merged);
  }, []);

  const sendAction = useCallback(async (
    type: ConfigActionType,
    payload: Record<string, unknown>,
  ) => {
    const epochAtSend = selectionEpochRef.current;
    const run = async () => {
      const cfg = configRef.current;
      const sid = sessionRef.current;
      if (!sid || !cfg) return;
      const ctx = createContractContext({
        sessionId: sid,
        productId: cfg.productId,
        revision: cfg.revision,
        workingConfigurationId: cfg.workingConfigurationId,
      });
      const action: ConfigAction = {
        commandId: generateId('cmd'),
        type,
        payload,
        context: ctx,
      };
      try {
        const base = getPlayerApiBase();
        const res = await fetch(`${base}/api/configurations/actions`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(action),
        });
        const data = await res.json();
        if (res.status === 409 && data.configurationState) {
          applyServerConfiguration(data.configurationState, epochAtSend);
          const latest = configRef.current ?? data.configurationState;
          const retryCtx = createContractContext({
            sessionId: sid,
            productId: latest.productId,
            revision: latest.revision,
            workingConfigurationId: latest.workingConfigurationId,
          });
          const retryRes = await fetch(`${base}/api/configurations/actions`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ...action, commandId: generateId('cmd'), context: retryCtx }),
          });
          const retryData = await retryRes.json();
          if (retryRes.ok) {
            applyServerConfiguration(retryData.configurationState, epochAtSend);
          }
          return;
        }
        if (!res.ok) throw new Error(data.error?.message || 'Action failed');
        applyServerConfiguration(data.configurationState, epochAtSend);
      } catch (e: any) {
        setError(e.message);
      }
    };
    pendingRef.current = pendingRef.current.then(run);
    await pendingRef.current;
  }, [applyServerConfiguration]);

  const setAttribute = useCallback(async (attributeId: string, value: string | number | boolean) => {
    let id = attributeId;
    let nextValue = value;
    if (attributeId === 'custom_color') {
      id = 'gemstone';
    } else if (attributeId === 'color') {
      if (value === 'gradient') {
        id = 'material';
        nextValue = 'gradient';
      } else {
        id = 'gemstone';
      }
    } else if (attributeId === 'engraving_dots' || attributeId === 'engraving_on_off') {
      if (value === 'true' || value === true || value === 1 || value === '1') nextValue = true;
      else if (value === 'false' || value === false || value === 0 || value === '0') nextValue = false;
    }
    const cfg = configRef.current;
    const leavingGradient =
      id === 'gemstone' &&
      cfg?.selections?.material === 'gradient' &&
      nextValue !== 'gradient';
    if (cfg && cfg.selections[id] === nextValue && !leavingGradient) return;
    if (cfg) {
      selectionEpochRef.current += 1;
      const newSelections = { ...cfg.selections, [id]: nextValue };
      if (leavingGradient) newSelections.material = 'carpaint';
      const evaluated =
        ruleGraphsRef.current.length > 0
          ? evaluateRuleGraphsForSelections(ruleGraphsRef.current, newSelections, productModelRef.current)
          : null;
      const ruleActions: RuleAction[] =
        evaluated ?? ((cfg.derived?.ruleActions as RuleAction[]) ?? []);
      const optimistic: ConfigurationState = {
        ...cfg,
        selections: newSelections,
        derived: { ...cfg.derived, ruleActions },
      };
      configRef.current = optimistic;
      setConfigState(optimistic);
    }
    await sendAction('setAttributeValue', { attributeId: id, value: nextValue });
    // Restore carpaint after gemstone so scene rules (material=gradient) stay intact.
    if (leavingGradient) {
      await sendAction('setAttributeValue', { attributeId: 'material', value: 'carpaint' });
    }
  }, [sendAction]);

  const selectComponent = useCallback(async (componentId: string, selectedVariantId: string) => {
    const cfg = configRef.current;
    if (cfg) {
      selectionEpochRef.current += 1;
      const newSelections = { ...cfg.selections, [`component:${componentId}`]: selectedVariantId };
      const evaluated =
        ruleGraphsRef.current.length > 0
          ? evaluateRuleGraphsForSelections(ruleGraphsRef.current, newSelections, productModelRef.current)
          : null;
      const ruleActions: RuleAction[] =
        evaluated ?? ((cfg.derived?.ruleActions as RuleAction[]) ?? []);
      const optimistic: ConfigurationState = {
        ...cfg,
        selections: newSelections,
        derived: { ...cfg.derived, ruleActions },
      };
      configRef.current = optimistic;
      setConfigState(optimistic);
    }
    await sendAction('selectComponent', { componentId, selectedVariantId });
  }, [sendAction]);

  const resetConfiguration = useCallback(async () => {
    await sendAction('resetConfiguration', {});
  }, [sendAction]);

  return {
    sessionId,
    productModel,
    configState,
    assets,
    componentVariants,
    sceneData,
    libraryMaterials,
    getRuleGraphs,
    loading,
    error,
    initSession,
    setAttribute,
    selectComponent,
    resetConfiguration,
  };
}
